import {
  Button,
  Circle,
  Stack,
  type UseRadioProps,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import React from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { isCorrectAnswer } from '@/client/services/assessment'

import FormContentBlock from '@/components/UI/Form/FormContentBlock'
import { FormContext, type FormInputType } from '@/components/UI/Form/types'

const ANSWER_STATE_TO_CONFIG = {
  none: {
    bg: 'bg.subtle',
    color: 'fg.muted',
  },
  'selected-unknown': {
    bg: 'primary.solid',
    color: 'primary.contrast',
  },
  'selected-incorrect': {
    bg: 'error.solid',
    color: 'error.contrast',
  },
  'selected-correct': {
    bg: 'success.solid',
    color: 'success.contrast',
  },
  'unselected-correct': {
    bg: 'success.solid',
    color: 'success.contrast',
  },
} as const

interface MessageEmbedV1AssessmentInputRadioItemProps extends UseRadioProps {
  optionName: string | null
  isDisabled?: boolean
  formInput: FormInputType & { type: 'radio' }
  label: (FormInputType & { type: 'radio' })['label']
}

function MessageEmbedV1AssessmentInputRadioItem({
  optionName,
  isDisabled = false,
  formInput,
  label,
  ...props
}: MessageEmbedV1AssessmentInputRadioItemProps) {
  const { getInputProps, getRadioProps } = useRadio(props)

  const input = getInputProps()
  const radio = getRadioProps()

  let state: keyof typeof ANSWER_STATE_TO_CONFIG = 'none'
  const isChecked = Boolean(input.checked)

  if (isDisabled) {
    const isCorrect = isCorrectAnswer({ input: formInput, response: input.value })

    if (isCorrect === null) {
      state = isChecked ? 'selected-unknown' : 'none'
    } else if (isCorrect) {
      state = isChecked ? 'selected-correct' : 'unselected-correct'
    } else {
      state = isChecked ? 'selected-incorrect' : 'none'
    }
  } else if (isChecked) {
    state = 'selected-unknown'
  }

  return (
    <Button
      as="label"
      {...radio}
      _hover={{
        borderColor: isDisabled ? '' : 'primary.focusRing',
      }}
      _active={{
        borderColor: isDisabled ? '' : 'primary.focusRing',
      }}
      _checked={{
        borderColor: ANSWER_STATE_TO_CONFIG[state].bg,
      }}
      bg="none"
      border={`2px solid ${chakraTokenToVar('border')}`}
      color="fg"
      alignItems="center"
      justifyContent="flex-start"
      flex={1}
      css={css`p { margin: 0; }`}
      p="16px"
    >
      {optionName == null ? null : (
        <Circle
          size="36px"
          bg={ANSWER_STATE_TO_CONFIG[state].bg}
          color={ANSWER_STATE_TO_CONFIG[state].color}
          marginRight="26px"
        >
          {optionName}
        </Circle>
      )}

      <FormContentBlock content={label} />
      <input {...input} disabled={isDisabled} />
    </Button>
  )
}

const MessageEmbedV1AssessmentInputRadio: React.FC<{
  input: FormInputType & { type: 'radio' }
}> = ({ input }) => {
  const { name: inputName } = input
  const { submitting, disabled, onChange } = React.useContext(FormContext)
  const [value, setValue] = React.useState(input.initialValue ?? null)
  const handleChange = React.useCallback(
    (newValue: string) => {
      onChange(inputName, newValue)
      setValue(newValue)
    },
    [inputName, onChange]
  )
  const isDisabled = submitting || input.disabled === true || disabled

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: inputName,
    value: value ?? input.initialValue ?? undefined,
    onChange: handleChange,
    isDisabled,
  })

  const group = getRootProps()
  const showOptionName = input.options.length <= 4

  return (
    <Stack {...group} spacing="8px">
      {input.options.map((item, index) => (
        <MessageEmbedV1AssessmentInputRadioItem
          key={item.value}
          {...getRadioProps({ value: item.value })}
          formInput={input}
          label={item.label}
          isDisabled={isDisabled}
          optionName={showOptionName ? String.fromCharCode(index + 65) : null}
        />
      ))}
    </Stack>
  )
}

export default MessageEmbedV1AssessmentInputRadio
